/**
 * E2 Selfservice — types + Zod-schemas voor mutatieverzoeken.
 *
 * Een MutatieVerzoek is een aanvraag van een medewerker om één van zijn
 * eigen Employee-velden te wijzigen. Pas na goedkeuring door HR wordt de
 * Employee-record daadwerkelijk geüpdatet.
 *
 * Bron: spec E2 Selfservice + HANDOVER §5.11.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
// FO-340 — laag 2 in het eigen profiel (read-only, zonder non-actief-reden).
import type { Werkstatus } from './medewerker-status'

// ─── Enums (matching Prisma enums als string unions) ─────────────────────────

export const MUTATIE_TYPE = ['ADRES', 'BANK', 'NOODCONTACT', 'TELEFOON', 'EMAIL', 'VERLOFOPBOUW', 'KIND', 'PRIVE_TELEFOON', 'PRIVE_EMAIL'] as const
export type MutatieType = (typeof MUTATIE_TYPE)[number]

export const MUTATIE_STATUS = [
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'INGETROKKEN',
  // batch-46 adres-reis (FO-390) — ADRES-mutatie met toekomstige verhuisdatum.
  'GEPLAND',
] as const
export type MutatieStatus = (typeof MUTATIE_STATUS)[number]

export const MUTATIE_AUDIT_ACTIE = [
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'INGETROKKEN',
  'EMPLOYEE_BIJGEWERKT',
] as const
export type MutatieAuditActie = (typeof MUTATIE_AUDIT_ACTIE)[number]

// ─── Label-records ───────────────────────────────────────────────────────────

export const MUTATIE_TYPE_LABELS: Record<MutatieType, string> = {
  ADRES:       'Adres',
  BANK:        'Bankrekening',
  NOODCONTACT: 'Noodcontact',
  TELEFOON:    'Telefoonnummer',
  EMAIL:       'E-mailadres',
  // batch-68 (FO-344 §3.4): in gewone taal, met de afkortingen als herkenningspunt.
  VERLOFOPBOUW: 'Werktijd en sparen (ATV/TVT)',
  KIND:        'Kind toevoegen (gezin)',
  PRIVE_TELEFOON: 'Privé telefoonnummer',
  PRIVE_EMAIL:    'Privé e-mailadres',
}

export const MUTATIE_STATUS_LABELS: Record<MutatieStatus, string> = {
  INGEDIEND:    'Ingediend',
  GOEDGEKEURD:  'Goedgekeurd',
  AFGEKEURD:    'Afgekeurd',
  INGETROKKEN:  'Ingetrokken',
  GEPLAND:      'Gepland',
}

export const MUTATIE_AUDIT_ACTIE_LABELS: Record<MutatieAuditActie, string> = {
  INGEDIEND:           'Verzoek ingediend',
  GOEDGEKEURD:         'Goedgekeurd door HR',
  AFGEKEURD:           'Afgekeurd door HR',
  INGETROKKEN:         'Ingetrokken door medewerker',
  EMPLOYEE_BIJGEWERKT: 'Employee-record bijgewerkt',
}

export const MUTATIE_STATUS_BADGE_VARIANT: Record<
  MutatieStatus,
  'info' | 'success' | 'danger' | 'secondary'
> = {
  INGEDIEND:   'info',
  GOEDGEKEURD: 'success',
  AFGEKEURD:   'danger',
  INGETROKKEN: 'secondary',
  GEPLAND:     'info',
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface MutatieVerzoekListRowDTO {
  id: string
  employeeId: string
  employeeName: string
  type: MutatieType
  status: MutatieStatus
  ingediendOp: string             // ISO timestamp
  reviewedAt: string | null
  reviewedByName: string | null
}

export interface MutatieAuditDTO {
  id: string
  actie: MutatieAuditActie
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

export interface MutatieVerzoekDetailDTO {
  id: string
  employeeId: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string
  }
  type: MutatieType
  status: MutatieStatus
  oudeWaarde: Record<string, unknown>
  nieuweWaarde: Record<string, unknown>
  toelichting: string | null
  reviewedBy: string | null
  reviewedByName: string | null
  reviewedAt: string | null
  reviewNote: string | null
  ingediendOp: string
  /**
   * FO-525 A9: de TVT-vlaggen gaan al eerder in dan de gevraagde ingangsdatum
   * (yyyy-mm-dd, 1 december); null = alles op de gevraagde ingangsdatum.
   */
  gevraagdeTvtIngangsdatum: string | null
  /**
   * batch-70 (FO-525): de gevraagde ingangsdatum (yyyy-mm-dd), bijvoorbeeld 1 januari
   * bij een keuze uit de vakantiecampagne; null = geen voorkeur (directe ingang).
   */
  gevraagdeIngangsdatum?: string | null
  audits: MutatieAuditDTO[]
  createdAt: string
  updatedAt: string
}

export interface MijnProfielDTO {
  adres: {
    street: string | null
    houseNumber: string | null
    postalCode: string | null
    city: string | null
  }
  bank: {
    iban: string | null
    bankTnvNaam: string | null
  }
  noodcontact: {
    naam: string | null
    relatie: string | null
    telefoon: string | null
  }
  telefoon: string | null
  email: string | null
  /** Privé-contact (eigen record). Bewerkbaar via PRIVE_TELEFOON/PRIVE_EMAIL-mutatieverzoek. */
  priveTelefoon: string | null
  priveEmail: string | null
  /** Read-only kerngegevens (correctie via HR in de backoffice). */
  persoonsgegevens: {
    geboortedatum: string | null
    geboortePlaats: string | null
    nationaliteit: string | null
    burgerlijkeStaat: string | null
  }
  /** Bellijst-privacy — of de medewerker zichtbaar is in de interne collega-bellijst. */
  zichtbaarInBellijst: boolean
  /**
   * FO-340 §10.4 — de eigen werkstatus, read-only. De medewerker ziet in de
   * portal dat hij ziek/non-actief staat en kan doorklikken naar zijn eigen
   * ziekmelding.
   *
   * `nonActiefReden` staat hier BEWUST NIET, en mag hier ook nooit bij komen:
   * een non-actief-reden kan een schorsing zijn, en dat gesprek voert HR - niet
   * een badge in de app van de medewerker zelf. `nonActiefTot` mag wél: weten
   * tot wanneer je niet inzetbaar bent is praktische informatie, geen oordeel.
   */
  werkstatus: Werkstatus
  /** `null` = onbepaald (tot HR de stelling opheft) of niet non-actief. */
  nonActiefTot: string | null
  /**
   * Spec 6 §2 — kledingmaten (self-service, melding-only). Vrije tekst zodat
   * halve maten / breedte-notities mogelijk blijven. Wijziging triggert een
   * Van Ysselt-mail (geen goedkeuring).
   */
  maten: MatenDTO
}

/**
 * Spec 6 §2 — de 5 kledingmaten van de medewerker. Vrije-tekst velden
 * (`null` = niet ingevuld). Eén shape voor zowel lezen (in `MijnProfielDTO`)
 * als de `PUT /me/maten`-payload.
 */
export interface MatenDTO {
  kledingmaatBoven: string | null
  kledingmaatBroek: string | null
  schoenmaat: string | null
  handschoenmaat: string | null
  helmmaat: string | null
  /**
   * Vrije pasvorm-opmerking ("extra breed", "shirt M, jas L"). Hoort NIET in de
   * maatvelden zelf: die worden gekozen uit het catalogus-vocabulaire, en vrije
   * tekst daarin maakt de maat onmatchbaar in de portal-catalogus.
   */
  maatOpmerking: string | null
}

// ─── Zod payload-schemas per type ────────────────────────────────────────────
//
// Naast structurele aanwezigheid voeren we strict-validatie uit (NL-postcode,
// IBAN-mod-97, telefoon, email) — de exact dezelfde checks zitten in
// `@streep/utils/mutatie-validation` voor server-side gebruik.

const NL_POSTCODE_RE = /^\d{4}\s?[A-Z]{2}$/i
const NL_TELEFOON_RE = /^(?:\+31|0)[1-9]\d{8}$/

const ibanCheck = (raw: string): boolean => {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase()
  if (cleaned.length < 15 || cleaned.length > 34) return false
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned)) return false
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4)
  let numeric = ''
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0)
    if (code >= 48 && code <= 57) numeric += ch
    else if (code >= 65 && code <= 90) numeric += String(code - 55)
    else return false
  }
  try {
    // BigInt literals (97n / 1n) vereisen ES2020+; sommige consumers
    // (apps/backoffice) targeten ES2017. Gebruik daarom de constructor.
    return BigInt(numeric) % BigInt(97) === BigInt(1)
  } catch {
    return false
  }
}

const telSanitize = (raw: string): string => raw.replace(/[\s\-().]/g, '')

const adresPayloadSchema = z.object({
  street:      z.string().trim().min(1, 'Straat is verplicht').max(120),
  houseNumber: z.string().trim().min(1, 'Huisnummer is verplicht').max(20),
  postalCode:  z.string().trim().regex(NL_POSTCODE_RE, 'Ongeldige NL-postcode'),
  city:        z.string().trim().min(1, 'Plaats is verplicht').max(80),
})

const bankPayloadSchema = z.object({
  iban:        z.string().trim().min(1).refine(ibanCheck, 'Ongeldige IBAN'),
  bankTnvNaam: z.string().trim().min(1, 'Tenaamstelling is verplicht').max(120),
})

const noodcontactPayloadSchema = z.object({
  noodcontactNaam:     z.string().trim().min(1, 'Naam is verplicht').max(120),
  noodcontactRelatie:  z.string().trim().min(1, 'Relatie is verplicht').max(60),
  noodcontactTelefoon: z
    .string()
    .trim()
    .min(1)
    .refine((v) => NL_TELEFOON_RE.test(telSanitize(v)), 'Ongeldig NL-telefoonnummer'),
})

const telefoonPayloadSchema = z.object({
  phone: z
    .string()
    .trim()
    .min(1)
    .refine((v) => NL_TELEFOON_RE.test(telSanitize(v)), 'Ongeldig NL-telefoonnummer'),
})

const emailPayloadSchema = z.object({
  email: z.string().trim().min(1).email('Ongeldig e-mailadres').max(254),
})

// Privé-contact (selfservice, HR-goedkeuring). Privé-telefoon = zelfde NL-
// telefooncheck als werk-telefoon; privé-e-mail = standaard e-mailcheck. Keys
// matchen de Employee-velden (privatePhone/privateEmail) zodat snapshot/diff/
// FIELD_LABELS uniform blijven.
const priveTelefoonPayloadSchema = z.object({
  privatePhone: z
    .string()
    .trim()
    .min(1)
    .refine((v) => NL_TELEFOON_RE.test(telSanitize(v)), 'Ongeldig NL-telefoonnummer'),
})

const priveEmailPayloadSchema = z.object({
  privateEmail: z.string().trim().min(1).email('Ongeldig e-mailadres').max(254),
})

/**
 * CAO-invariant: TVT (werk én reis) kan alleen opgebouwd worden als ATV-opbouw
 * aanstaat. Eén bron voor de portal-form, de submit-API en de contractmutatie.
 * `true` = de combinatie is geldig.
 */
export const TVT_VEREIST_ATV_MESSAGE =
  'TVT-opbouw kan alleen aanstaan als ATV-opbouw aanstaat'

export function verlofOpbouwVlaggenGeldig(flags: {
  atvOpbouw: boolean
  tvtWerk: boolean
  tvtReis: boolean
}): boolean {
  return flags.atvOpbouw || (!flags.tvtWerk && !flags.tvtReis)
}

const verlofopbouwPayloadSchema = z
  .object({
    atvOpbouw: z.boolean(),
    tvtWerk: z.boolean(),
    tvtReis: z.boolean(),
    // FO-122 — gekozen opbouwpercentage per TVT-soort (50 of 100). Default 100 =
    // volledige opbouw. Reist mee in het verzoek; bij TVT-uit negeert de API het
    // (normaliseert naar 100). Alleen 50/100 toegestaan (geen vrije invoer).
    tvtWerkPercentage: z.union([z.literal(50), z.literal(100)]).default(100),
    tvtReisPercentage: z.union([z.literal(50), z.literal(100)]).default(100),
  })
  .superRefine((val, ctx) => {
    if (verlofOpbouwVlaggenGeldig(val)) return
    // Fout op het/de aangevinkte TVT-veld(en) zodat de portal hem inline toont.
    for (const key of ['tvtWerk', 'tvtReis'] as const) {
      if (val[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: TVT_VEREIST_ATV_MESSAGE,
        })
      }
    }
  })

// Batch-6 verlof — selfservice "kind toevoegen" (ouderschapsverlof). De flow
// is melding-only (classificatie KIND): de EmployeeChild-rij + het recht
// ontstaan direct bij indienen; HR krijgt een KIND_TOEGEVOEGD-notificatie en
// corrigeert achteraf waar nodig. AVG: kindgegevens nooit loggen.
export const EMPLOYEE_CHILD_RELATIE = ['KIND', 'PLEEGKIND', 'ADOPTIEKIND', 'STIEFKIND'] as const
export type EmployeeChildRelatie = (typeof EMPLOYEE_CHILD_RELATIE)[number]

export const EMPLOYEE_CHILD_RELATIE_LABELS: Record<EmployeeChildRelatie, string> = {
  KIND:        'Kind',
  PLEEGKIND:   'Pleegkind',
  ADOPTIEKIND: 'Adoptiekind',
  STIEFKIND:   'Stiefkind',
}

const kindPayloadSchema = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht').max(200),
  // ISO-datum (yyyy-mm-dd); de API bewaart UTC-middernacht van de NL-dag.
  geboortedatum: z.string().date('Ongeldige geboortedatum'),
  relatie: z.enum(EMPLOYEE_CHILD_RELATIE).default('KIND'),
})

export {
  adresPayloadSchema,
  bankPayloadSchema,
  noodcontactPayloadSchema,
  telefoonPayloadSchema,
  emailPayloadSchema,
  priveTelefoonPayloadSchema,
  priveEmailPayloadSchema,
  verlofopbouwPayloadSchema,
  kindPayloadSchema,
}

// ─── Spec 6 §2 — Kledingmaten (self-service, melding-only) ────────────────────
//
// Vrije-tekst velden (halve maten / breedte-notities mogelijk). Lege string
// wordt naar `null` genormaliseerd zodat "wissen" mogelijk is. Geen
// regex/lengte-strakheid behalve een ruime max om wartaal te weren.
const matenVeld = z
  .string()
  .trim()
  .max(40)
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional()
  .transform((v) => v ?? null)

// Pasvorm-/maatopmerking: bewust ruimer dan een maatveld, want hier hoort de
// vrije tekst thuis die eerder de maatvelden zelf onmatchbaar maakte.
const maatOpmerkingVeld = z
  .string()
  .trim()
  .max(500)
  .transform((v) => (v.length === 0 ? null : v))
  .nullable()
  .optional()
  .transform((v) => v ?? null)

export const updateMatenSchema = z.object({
  kledingmaatBoven: matenVeld,
  kledingmaatBroek: matenVeld,
  schoenmaat: matenVeld,
  handschoenmaat: matenVeld,
  helmmaat: matenVeld,
  maatOpmerking: maatOpmerkingVeld,
})
export type UpdateMatenInput = z.infer<typeof updateMatenSchema>

// ─── Discriminated union voor submit ─────────────────────────────────────────

const toelichtingField = z.string().trim().max(500).optional()

// batch-46 adres-reis (FO-390) — verhuisdatum op een ADRES-mutatie (yyyy-mm-dd).
// Optioneel voor achterwaartse compat; de API valt terug op vandaag als hij
// ontbreekt. Het toegestane bereik (−3 mnd t/m +12 mnd) hangt van "vandaag" af en
// wordt server-side + in de form gecheckt via `verhuisdatumBinnenBereik`
// (@streep/utils), niet hier (Zod kent de NL-vandaag niet).
const verhuisdatumField = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige verhuisdatum (yyyy-mm-dd)')
  .optional()

export const submitMutatieSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ADRES'),       payload: adresPayloadSchema,       verhuisdatum: verhuisdatumField, toelichting: toelichtingField }),
  z.object({ type: z.literal('BANK'),        payload: bankPayloadSchema,        toelichting: toelichtingField }),
  z.object({ type: z.literal('NOODCONTACT'), payload: noodcontactPayloadSchema, toelichting: toelichtingField }),
  z.object({ type: z.literal('TELEFOON'),    payload: telefoonPayloadSchema,    toelichting: toelichtingField }),
  z.object({ type: z.literal('EMAIL'),       payload: emailPayloadSchema,       toelichting: toelichtingField }),
  z.object({ type: z.literal('VERLOFOPBOUW'), payload: verlofopbouwPayloadSchema, toelichting: toelichtingField }),
  z.object({ type: z.literal('KIND'),        payload: kindPayloadSchema,        toelichting: toelichtingField }),
  z.object({ type: z.literal('PRIVE_TELEFOON'), payload: priveTelefoonPayloadSchema, toelichting: toelichtingField }),
  z.object({ type: z.literal('PRIVE_EMAIL'),    payload: priveEmailPayloadSchema,    toelichting: toelichtingField }),
])
export type SubmitMutatieInput = z.infer<typeof submitMutatieSchema>

// ─── batch-46 adres-reis (FO-390) — geocode-check + backoffice adreswijziging ──
//
// De verhuisdatum + geocode-validatie zijn er om te voorkomen dat een tikfout in
// postcode/huisnummer stil een woon-werk-herberekening voedt. Dezelfde adres-
// invoer valideren portal én backoffice; de geocode-check geeft het
// genormaliseerde adres ter bevestiging terug ("Bedoel je: ...?").

/** Adres-invoer voor de geocode-controle + de backoffice-adreswijziging. */
export const adresGeocodeInputSchema = z.object({
  street:      z.string().trim().min(1, 'Straat is verplicht').max(120),
  houseNumber: z.string().trim().min(1, 'Huisnummer is verplicht').max(20),
  postalCode:  z.string().trim().regex(NL_POSTCODE_RE, 'Ongeldige NL-postcode'),
  city:        z.string().trim().min(1, 'Plaats is verplicht').max(80),
})
export type AdresGeocodeInput = z.infer<typeof adresGeocodeInputSchema>

/** Genormaliseerd adres zoals de geocoder het teruggeeft (ter bevestiging). */
export interface AdresGeocodeGenormaliseerd {
  street: string | null
  houseNumber: string | null
  postalCode: string | null
  city: string | null
  /** Volledige, leesbare regel: "Dorpsstraat 12, 1234 AB Ons Dorp". */
  formatted: string
}

/** Antwoord van de geocode-controle. `ok=false` -> inline veldfout tonen. */
export interface AdresGeocodeResultDTO {
  ok: boolean
  /**
   * Was de geocoder bereikbaar? `false` = geen key / storing (kon niet valideren).
   * Bij `kanValideren=false` mag de client gewoon doorgaan (best-effort); alleen bij
   * `kanValideren=true && ok=false` is er een echte tikfout die geweigerd moet worden.
   */
  kanValideren: boolean
  /** Alleen bij ok: het genormaliseerde adres ter bevestiging. */
  genormaliseerd: AdresGeocodeGenormaliseerd | null
  latitude: number | null
  longitude: number | null
  placeId: string | null
  /** Bij !ok: nette uitleg voor de inline veldfout. */
  message: string | null
}

/** Backoffice-adreswijziging (HR): adres + verhuisdatum. Deelt de portal-triggers. */
export const backofficeAdresWijzigingSchema = z.object({
  street:       z.string().trim().min(1, 'Straat is verplicht').max(120),
  houseNumber:  z.string().trim().min(1, 'Huisnummer is verplicht').max(20),
  postalCode:   z.string().trim().regex(NL_POSTCODE_RE, 'Ongeldige NL-postcode'),
  city:         z.string().trim().min(1, 'Plaats is verplicht').max(80),
  verhuisdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige verhuisdatum (yyyy-mm-dd)'),
  toelichting:  z.string().trim().max(500).optional(),
  // HR-uitzondering (spec 93 §3.2): bij hoge uitzondering vastleggen zonder
  // geocode-match, met expliciete waarschuwing + audit. Default false.
  negeerGeocode: z.boolean().optional(),
})
export type BackofficeAdresWijzigingInput = z.infer<typeof backofficeAdresWijzigingSchema>

/** Uitkomst van een (portal- of backoffice-)adreswijziging. */
export interface AdresWijzigingResultDTO {
  id: string
  status: MutatieStatus
  /** true = direct toegepast (vandaag/verleden); false = GEPLAND (toekomst). */
  toegepast: boolean
  /** yyyy-mm-dd van de verhuisdatum. */
  ingangsdatum: string
}

// ─── Review + withdraw + list query ─────────────────────────────────────────

export const reviewMutatieSchema = z
  .object({
    decision: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
    note:     z.string().trim().max(500).optional(),
    // VERLOFOPBOUW-only: bij goedkeuren automatisch de effective-dated
    // contractmutatie aanmaken die de ATV/TVT-opbouwvlaggen vastlegt (zodat HR
    // dit niet meer handmatig hoeft te doen). Server-default = true. Genegeerd
    // voor andere types en bij afkeuren.
    autoContractmutatie: z.boolean().optional(),
    // DIRECT = vandaag; VANAF = ingangsdatum kiezen (gepland of TWK).
    ingang:       z.enum(['DIRECT', 'VANAF']).optional(),
    ingangsdatum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (yyyy-mm-dd)')
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (val.decision === 'AFGEKEURD' && (!val.note || val.note.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: 'Reden van afkeur is verplicht',
      })
    }
    if (val.ingang === 'VANAF' && !val.ingangsdatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ingangsdatum'],
        message: 'Kies een ingangsdatum',
      })
    }
  })

export type ReviewMutatieInput = z.infer<typeof reviewMutatieSchema>

export const withdrawMutatieSchema = z.object({
  reden: z.string().trim().max(500).optional(),
})
export type WithdrawMutatieInput = z.infer<typeof withdrawMutatieSchema>

export const listMutatieQuerySchema = z.object({
  status:     z.enum(MUTATIE_STATUS).optional(),
  type:       z.enum(MUTATIE_TYPE).optional(),
  employeeId: idParam().optional(),
  // scope=mine dwingt server-side filtering op de eigen Employee af, ongeacht
  // backoffice-access. De portal stuurt dit altijd mee; zonder scope toont de
  // backoffice-beheerlijst iedereen.
  scope:      z.enum(['mine']).optional(),
  vanaf:      z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  totEnMet:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page:       z.coerce.number().int().positive().default(1),
  pageSize:   z.coerce.number().int().positive().max(2000).default(20),
})
export type ListMutatieQuery = z.infer<typeof listMutatieQuerySchema>

// ─── Fase 7.4 — Bulk-review ─────────────────────────────────────────────────
//
// HR beoordeelt meerdere mutatieverzoeken tegelijk. Server-side wordt elk
// verzoek individueel verwerkt (transactie per verzoek) zodat één fout de
// rest niet blokkeert; response bevat per-id outcome.

export const bulkReviewMutatieSchema = z
  .object({
    ids:      z.array(idParam()).min(1).max(50),
    decision: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
    note:     z.string().trim().max(500).optional(),
    // VERLOFOPBOUW-only: idem als enkel-review, maar bulk kent geen per-regel
    // datumkeuze → altijd directe ingang (vandaag). Server-default = true.
    autoContractmutatie: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.decision === 'AFGEKEURD' && (!val.note || val.note.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: 'Reden van afkeur is verplicht bij bulk-afkeur',
      })
    }
  })
export type BulkReviewMutatieInput = z.infer<typeof bulkReviewMutatieSchema>

export interface BulkReviewMutatieResultRow {
  id: string
  ok: boolean
  /** Wanneer ok=false: code (NOT_FOUND, INVALID_STATE, INTERNAL). */
  errorCode?: 'NOT_FOUND' | 'INVALID_STATE' | 'INTERNAL'
  errorMessage?: string
}

export interface BulkReviewMutatieResultDTO {
  total: number
  succeeded: number
  failed: number
  results: BulkReviewMutatieResultRow[]
}

// ─── Fase 7.4 — Email-confirm-flow ──────────────────────────────────────────
//
// Wanneer een mutatieverzoek van type EMAIL wordt aangevraagd, sturen we eerst
// een bevestigings-link naar het NIEUWE e-mailadres. Pas na klikken op de link
// (binnen 24u) gaat het verzoek naar HR. Zonder bevestiging blijft het in
// status WACHT_BEVESTIGING (modus-uitbreiding zonder enum-wijziging — we
// gebruiken `toelichting` als marker tot een vervolg-iteratie).
//
// Voor scope 7.4: een simpele endpoint die het token verzilvert en daarna de
// normale review-flow start. Het token-model komt in een vervolg-migratie als
// dat nog niet bestaat — voor MVP hergebruiken we Document-storage of een
// in-memory map met TTL. Hier definiëren we alleen de schema's voor de API.

export const requestEmailConfirmSchema = z.object({
  mutatieVerzoekId: idParam(),
  /** Nieuwe e-mail die geverifieerd moet worden. */
  newEmail: z.string().email().max(200),
})
export type RequestEmailConfirmInput = z.infer<typeof requestEmailConfirmSchema>

export const confirmEmailTokenSchema = z.object({
  token: z.string().min(16).max(256),
})
export type ConfirmEmailTokenInput = z.infer<typeof confirmEmailTokenSchema>
